import { Component } from '@theme/component';
import {
  fetchConfig,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

let cartMutationQueue = Promise.resolve();
let pendingCartMutations = 0;
let cartNeedsRefresh = false;
let cartFocusToRestore = null;

function restoreCartFocus() {
  const saved = cartFocusToRestore;
  cartFocusToRestore = null;
  if (!saved || (document.activeElement !== document.body && document.activeElement !== saved.element)) return;

  const component = Array.from(document.querySelectorAll('cart-items-component')).find(
    (element) => element.dataset.sectionId === saved.sectionId && element.hasAttribute('data-drawer') === saved.isDrawer
  );
  if (!component || component.closest('dialog:not([open])')) return;

  const rows = Array.from(component.querySelectorAll('[ref="cartItemRows[]"]'));
  const row = rows.find((element) => element.dataset.key === saved.lineId);
  const control = saved.isRemove ? '.cart-items__remove' : `[ref="${CSS.escape(saved.ref)}"]`;
  const target = cartNeedsRefresh
    ? component.querySelector('[data-cart-sync-reload]:not([hidden])')
    : row?.querySelector(`${control}:not(:disabled)`) ||
      (row || rows[Math.min(saved.index, rows.length - 1)])?.querySelector('[ref="quantityInput"]') ||
      component.querySelector('.cart-items__empty-button');

  if (target instanceof HTMLElement && !target.closest('[inert]') && target.getClientRects().length) {
    target.focus({ preventScroll: true });
  }
}

function syncCartMutationLock() {
  const busy = pendingCartMutations > 0 || cartNeedsRefresh;
  document.querySelectorAll('cart-items-component').forEach((component) => {
    component.setAttribute('aria-busy', String(busy));
    component.classList.toggle('cart-items-disabled', busy);
    component.querySelectorAll('.cart-form').forEach((form) => { form.inert = busy; });
  });
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #onQuantityEvent = (event) => {
    if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
  };
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityEvent);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    syncCartMutationLock();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityEvent);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const lineId = this.refs.cartItemRows?.[config.line - 1]?.dataset.key;
    if (!lineId || cartNeedsRefresh || !Number.isInteger(config.quantity) || config.quantity < 0) return;

    const active = document.activeElement;
    const focusedRow = active?.closest('[ref="cartItemRows[]"]');
    if (pendingCartMutations === 0 && active instanceof HTMLElement && focusedRow && this.contains(active)) {
      cartFocusToRestore = {
        element: active,
        sectionId: this.sectionId,
        isDrawer: this.isDrawer,
        lineId: focusedRow.dataset.key,
        index: this.refs.cartItemRows.indexOf(focusedRow),
        ref: active.getAttribute('ref') || 'quantityInput',
        isRemove: active.classList.contains('cart-items__remove'),
      };
    }

    pendingCartMutations++;
    syncCartMutationLock();
    const operation = cartMutationQueue.then(async () => {
      if (!cartNeedsRefresh) await this.#performQuantityUpdate({ ...config, lineId });
    });
    cartMutationQueue = operation.catch(() => { cartNeedsRefresh = true; });
    return cartMutationQueue.finally(() => {
      pendingCartMutations--;
      syncCartMutationLock();
      if (pendingCartMutations === 0) restoreCartFocus();
    });
  }

  async #performQuantityUpdate(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const { lineId, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      id: lineId,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    let validationMessage;
    try {
      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const cart = await response.json();
      if (!response.ok || cart.errors) {
        validationMessage = typeof cart.errors === 'string' ? cart.errors : cart.description;
        throw new Error('Cart update was not confirmed');
      }
      await this.#applyCartState(cart);
      deferredUpdatePromise.resolve(this.#cartEventResult(cart, false));
    } catch (error) {
      try {
        const cart = await this.#readCartState(sectionsToUpdate);
        await this.#applyCartState(cart);
        deferredUpdatePromise.resolve(this.#cartEventResult(cart, true));
        this.#showSyncMessage(false, validationMessage);
      } catch (refreshError) {
        cartNeedsRefresh = true;
        this.#showSyncMessage(true);
        deferredUpdatePromise.reject(refreshError);
      }
    } finally {
      resetShimmer(this);
      syncCartMutationLock();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  async #readCartState(sectionIds) {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to refresh cart');
    const cart = await response.json();
    const requestedSectionIds = Array.from(sectionIds);
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.set('sections', requestedSectionIds.join(','));
    const rendered = await fetch(url, { cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!rendered.ok) throw new Error('Unable to refresh cart sections');
    const renderedSections = await rendered.json();
    let sections = renderedSections.sections || renderedSections;
    const hasRequestedSections = requestedSectionIds.every((id) => typeof sections?.[id] === 'string');

    if (!hasRequestedSections) {
      const entries = await Promise.all(requestedSectionIds.map(async (id) => {
        const sectionUrl = new URL(window.location.href);
        sectionUrl.searchParams.delete('sections');
        sectionUrl.searchParams.set('section_id', id);
        const sectionResponse = await fetch(sectionUrl, { cache: 'no-store' });
        if (!sectionResponse.ok) throw new Error('Unable to refresh cart section');
        return [id, await sectionResponse.text()];
      }));
      sections = Object.fromEntries(entries);
    }

    cart.sections = sections;
    return cart;
  }

  async #applyCartState(cart) {
    const components = Array.from(document.querySelectorAll('cart-items-component'));
    const sections = new Map();
    for (const component of components) {
      const id = component.dataset.sectionId;
      if (!id) continue;
      const html = cart.sections?.[id];
      if (typeof html !== 'string' || !html.includes(`shopify-section-${id}`)) throw new Error('Incomplete cart sections');
      sections.set(id, { html, mode: component.hasAttribute('data-drawer') ? 'hydration' : 'full' });
    }
    for (const [id, { html, mode }] of sections) {
      await morphSection(id, html, { mode, injectStylesheet: true });
    }
    this.#updateQuantitySelectors(cart);
    this.#updateCartQuantitySelectorButtonStates();
    syncCartMutationLock();
  }

  #cartEventResult(cart, didError) {
    return {
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { sections: cart.sections, items: cart.items, itemCount: cart.item_count, sectionsApplied: true, didError },
    };
  }

  #showSyncMessage(unavailable, message) {
    document.querySelectorAll('[data-cart-sync-status]').forEach((notice) => {
      notice.hidden = false;
      notice.querySelector('[data-cart-sync-text]').textContent = message ||
        (unavailable ? notice.dataset.unavailableMessage : notice.dataset.refreshMessage);
      notice.querySelector('[data-cart-sync-reload]').hidden = !unavailable;
    });
  }

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        if (detail?.sectionsApplied) return;
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
